import {
  PERMISSIONS,
  PERMISSION_LABELS,
  ROLE_LABELS,
  ROLE_PRESETS,
  roleForPermissions,
  type AssignableRole,
  type Permission,
} from '@hyphen-db-studio/access-core';

export interface Grant {
  role: AssignableRole;
  permissions: Permission[];
}

interface Props {
  value: Grant;
  onChange: (value: Grant) => void;
  /** Permissions the person editing is allowed to hand out (owners can grant everything). */
  grantable: readonly Permission[];
}

const ROLES: AssignableRole[] = ['viewer', 'editor', 'admin', 'custom'];

/** Role presets plus individual permission checkboxes: picking a role fills in the checkboxes, and ticking a box
 * that no longer matches a preset turns the role into "Custom". Viewing is always included for every member. */
export default function PermissionEditor({ value, onChange, grantable }: Props) {
  const canGrantAll = (perms: readonly Permission[]) => perms.every((p) => grantable.includes(p));

  function pickRole(role: AssignableRole) {
    if (role === 'custom') onChange({ role, permissions: value.permissions });
    else onChange({ role, permissions: [...ROLE_PRESETS[role]] });
  }

  function toggle(permission: Permission) {
    const permissions = value.permissions.includes(permission)
      ? value.permissions.filter((p) => p !== permission)
      : PERMISSIONS.filter((p) => p === permission || value.permissions.includes(p));
    onChange({ role: roleForPermissions(permissions), permissions });
  }

  return (
    <div>
      <div className="mb-3 inline-flex rounded-lg bg-gray-100 p-0.5 dark:bg-white/10">
        {ROLES.map((role) => {
          const allowed = role === 'custom' || canGrantAll(ROLE_PRESETS[role]);
          return (
            <button
              key={role}
              type="button"
              disabled={!allowed}
              onClick={() => pickRole(role)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                value.role === role
                  ? 'bg-white text-sky-700 shadow-sm dark:bg-gray-700 dark:text-sky-300'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
              }`}
            >
              {ROLE_LABELS[role]}
            </button>
          );
        })}
      </div>
      <div className="grid gap-1.5 sm:grid-cols-2">
        <label className="flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm opacity-60">
          <input type="checkbox" checked disabled className="mt-0.5" />
          <span>
            <span className="block font-medium dark:text-gray-200">View everything</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">Included for every member</span>
          </span>
        </label>
        {PERMISSIONS.map((permission) => {
          const allowed = grantable.includes(permission);
          return (
            <label
              key={permission}
              className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-sm ${allowed ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5' : 'cursor-not-allowed opacity-50'}`}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                disabled={!allowed}
                checked={value.permissions.includes(permission)}
                onChange={() => toggle(permission)}
              />
              <span>
                <span className="block font-medium dark:text-gray-200">{PERMISSION_LABELS[permission].label}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">{PERMISSION_LABELS[permission].description}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
